import React from 'react';
import Lottie from 'lottie-react';

// Import Lottie animations
import sunnyDayAnimation from '@/assets/weather/sunny-day.json';
import warmWindAnimation from '@/assets/weather/warm-wind.json';
import windyRainAnimation from '@/assets/weather/windy-rain.json';
import sunnyAnimation from '@/assets/weather/sunny.json';
import cloudsThunderAnimation from '@/assets/weather/clouds_1.json';
import rainyDayAnimation from '@/assets/weather/rainy-day.json';
import cloudsAnimation from '@/assets/weather/clouds.json';
import windyCloudAnimation from '@/assets/weather/windy-cloud.json';
import heavyRainAnimation from '@/assets/weather/heavy-rain.json';
import nightMoonAnimation from '@/assets/weather/night-moon.json';
import nightSkyAnimation from '@/assets/weather/night-sky.json';
import nightRainAnimation from '@/assets/weather/night-rain.json';

interface AnimatedWeatherIconProps {
  condition: string;
  className?: string;
  size?: number;
}

export const AnimatedWeatherIcon = ({ condition, className = "w-3 h-3", size = 24 }: AnimatedWeatherIconProps) => {
  const getWeatherAnimation = () => {
    const conditionLower = condition.toLowerCase();
    
    // Check if it's nighttime first - now includes specific night patterns from API
    const isNight = conditionLower.includes('night') || 
                   conditionLower.includes('clear night') ||
                   conditionLower.includes('partly cloudy night') ||
                   conditionLower.includes('cloudy night') ||
                   conditionLower.includes('night rain') ||
                   conditionLower.includes('night sky') ||
                   conditionLower.includes('night moon');
    
    if (isNight) {
      // Night + Rain conditions
      if (conditionLower.includes('night rain') || 
          (conditionLower.includes('night') && (conditionLower.includes('rain') || conditionLower.includes('shower')))) {
        return (
          <div className={className} style={{ width: size, height: size }}>
            <Lottie 
              animationData={nightRainAnimation} 
              loop={true}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        );
      }
      
      // Night + Cloudy conditions
      if (conditionLower.includes('night sky') || 
          (conditionLower.includes('night') && (conditionLower.includes('cloud') || conditionLower.includes('overcast')))) {
        return (
          <div className={className} style={{ width: size, height: size }}>
            <Lottie 
              animationData={nightSkyAnimation} 
              loop={true}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        );
      }
      
      // Night + Clear conditions (just moon) - default for any night condition
      return (
        <div className={className} style={{ width: size, height: size }}>
          <Lottie 
            animationData={nightMoonAnimation} 
            loop={true}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      );
    }
    
    // Rain conditions with wind
    if (conditionLower.includes('rain') && conditionLower.includes('wind')) {
      return (
        <div className={className} style={{ width: size, height: size }}>
          <Lottie 
            animationData={windyRainAnimation} 
            loop={true}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      );
    }
    
    // Rain conditions (use heavy rain animation)
    if (conditionLower.includes('rain') || conditionLower.includes('shower')) {
      return (
        <div className={className} style={{ width: size, height: size }}>
          <Lottie 
            animationData={heavyRainAnimation} 
            loop={true}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      );
    }
    
    // Drizzle conditions (use rainy day animation)
    if (conditionLower.includes('drizzle') || conditionLower.includes('mist')) {
      return (
        <div className={className} style={{ width: size, height: size }}>
          <Lottie 
            animationData={rainyDayAnimation} 
            loop={true}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      );
    }
    
    // Snow conditions (use clouds animation as snowstorm)  
    if (conditionLower.includes('snow') || conditionLower.includes('blizzard')) {
      return (
        <div className={className} style={{ width: size, height: size }}>
          <Lottie 
            animationData={cloudsAnimation} 
            loop={true}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      );
    }
    
    // Thunderstorm conditions
    if (conditionLower.includes('thunder') || conditionLower.includes('storm')) {
      return (
        <div className={className} style={{ width: size, height: size }}>
          <Lottie 
            animationData={cloudsThunderAnimation} 
            loop={true}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      );
    }
    
    // Cloudy conditions (use windy cloud animation)
    if (conditionLower.includes('cloud') || conditionLower.includes('overcast')) {
      return (
        <div className={className} style={{ width: size, height: size }}>
          <Lottie 
            animationData={windyCloudAnimation} 
            loop={true}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      );
    }
    
    // Foggy/Misty conditions
    if (conditionLower.includes('fog') || conditionLower.includes('haze')) {
      return (
        <div className={className} style={{ width: size, height: size }}>
          <Lottie 
            animationData={windyCloudAnimation} 
            loop={true}
            style={{ 
              filter: 'blur(1px) opacity(0.8)',
              width: '100%',
              height: '100%'
            }}
          />
        </div>
      );
    }
    
    // Windy conditions
    if (conditionLower.includes('wind')) {
      return (
        <div className={className} style={{ width: size, height: size }}>
          <Lottie 
            animationData={warmWindAnimation} 
            loop={true}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      );
    }
    
    // Sunny/Clear conditions (default)
    return (
      <div className={className} style={{ width: size, height: size }}>
        <Lottie 
          animationData={sunnyDayAnimation} 
          loop={true}
          style={{ width: '100%', height: '100%' }}
        />
      </div>
    );
  };

  return getWeatherAnimation();
};